// src/components/NotificationItem.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  HandHeart,
  Share2,
  MessageCircle,
  UserPlus,
  MessageSquare,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getNotificationNavigation } from '../utils/notificationRouting';
import './NotificationItem.css';

const getIcon = (type) => {
  switch (type) {
    case 'like':
    case 'record_reaction':
      return <Heart size={18} className="notification-icon like" />;
    case 'share':
      return <Share2 size={18} className="notification-icon share" />;
    case 'record_access_granted':
      return <HandHeart size={20} className="notification-icon access-granted" />;
    case 'comment':
    case 'record_comment':
      return <MessageCircle size={18} className="notification-icon comment" />;
    case 'follow':
      return <UserPlus size={18} className="notification-icon follow" />;
    case 'reply':
    case 'record_reply':
      return <MessageSquare size={18} className="notification-icon reply" />;
    case 'mention':
      return <MessageSquare size={18} className="notification-icon mention" />;
    default:
      return null;
  }
};

// Helper function to format time with i18n
const formatTime = (date, t) => {
  if (!date) return t('timeFormat.longTimeAgo');
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  
  if (diffMins < 1) {
    return t('timeFormat.justNow');
  }
  
  if (diffMins < 60) {
    return t('timeFormat.minutesAgo', { count: diffMins });
  }
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) {
    return t('timeFormat.hoursAgo', { count: diffHours });
  }
  
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return t('timeFormat.daysAgo', { count: diffDays });
  }
  
  // Fall back to date-fns for older dates
  return formatDistanceToNow(date, { addSuffix: false });
};

const NotificationItem = ({ notification, onClose }) => {
  const { t } = useTranslation();
  const { type, placeholders, createdAt, content } = notification;
  const navigate = useNavigate();
  
  const date = createdAt && createdAt.toDate ? 
    createdAt.toDate() : 
    (createdAt instanceof Date ? createdAt : new Date());
  
  const formattedDate = formatTime(date, t);

  // Get the localized notification text
  const notificationText = t(`notifications.${type}`, 
    placeholders || { userName: t('notifications.anonymous') });
  const secondaryText =
    typeof content === 'string' && content.trim().length > 0 ? content.trim() : null;

  const handleClick = () => {
    const navigation = getNotificationNavigation(notification);
    if (!navigation || !navigation.url) {
      return;
    }
    if (typeof onClose === 'function') {
      onClose(); // Close the notifications dropdown when provided
    }
    if (navigation.state) {
      navigate(navigation.url, { state: navigation.state });
    } else {
      navigate(navigation.url);
    }
  };

  return (
    <div className="notification-item" onClick={handleClick}>
      <div className="notification-icon-container">
        {getIcon(type)}
      </div>
      <div className="notification-content">
        <p className="notification-text">{notificationText}</p>
        {secondaryText && <p className="notification-secondary-text">{secondaryText}</p>}
        <span className="notification-time">{formattedDate}</span>
      </div>
    </div>
  );
};

export default NotificationItem;
